import { cn } from '@/utils';
import { type HTMLAttributes } from 'react';

/**
 * HeroSection — full-bleed page hero with optional gradient background.
 *
 * Handles two layout escape hatches automatically:
 *
 * 1. **Horizontal bleed** — `AppShell <main>` applies `px-5` (24px edge margins).
 *    `HeroSection` uses `-mx-[var(--spacing-5)]` to escape those margins so its
 *    background bleeds edge-to-edge. The `PageGrid className="px-5"` inside the
 *    hero then re-applies the edge margins for the hero's own content column.
 *
 * 2. **Vertical bleed behind AppHeader (desktop)** — `AppShell <main>` adds
 *    `padding-top: var(--app-header-height)` so content starts below the fixed
 *    header. On `md+` HeroSection uses `-mt-[var(--app-header-height)]` to pull
 *    itself back up behind the header, then `pt-[var(--app-header-height)]` to
 *    push its own content back down so it isn't obscured. The AppHeader has a
 *    translucent background (`bg-surface-translucent`), so the gradient shows
 *    through it naturally. On mobile the BottomTabBar is at the bottom — no top
 *    offset is needed.
 *
 * ### Usage
 * Render `HeroSection` as a direct child of `AppShell <main>` (i.e. as a top-level
 * element in an authenticated page). Place a `<PageGrid className="px-5">` inside
 * it to re-establish the content column with correct edge margins.
 *
 * ```tsx
 * // TODO: Replace hex gradient with design tokens when available
 * <HeroSection className="bg-gradient-to-br from-[#c8b4e8] to-[#f2cfc4]">
 *   <PageGrid className="px-5">
 *     <PageColumn className="pt-8 pb-0 flex flex-col items-center gap-3">
 *       <Avatar ... />
 *       <Typography ...>@username</Typography>
 *     </PageColumn>
 *   </PageGrid>
 * </HeroSection>
 * ```
 */
export interface HeroSectionProps extends HTMLAttributes<HTMLDivElement> {
  children?: React.ReactNode;
}

export function HeroSection({ className, children, ...props }: HeroSectionProps) {
  return (
    <div
      // Horizontal bleed: escape AppShell <main>'s px-5 edge margins
      // Vertical bleed (desktop only): pull up behind fixed AppHeader, push content back down
      className={cn(
        'col-span-12',
        '-mx-5',
        'md:-mt-(--app-header-height) md:pt-(--app-header-height)',
        className
      )}
      {...props}
    >
      {children}
    </div>
  );
}

export default HeroSection;
